import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MdVisibility, MdVisibilityOff, MdError } from "react-icons/md";
import { useAuthProvider } from "../../Providers/AuthProvider.js";
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../Providers/EndpointProvider";
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks";
import { SOFTWARE_VERSION } from "../../Version.js";
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont";
import bgImg from "../Account/Login/bgImg.png";
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner";
import darkLogoImg from "../Account/Login/moberg-logo-dark.png";

const RMLogin = ({ setNavPage }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const navigate = useNavigate();

    const authProvider = useAuthProvider();
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();

    useEffect(() => {
        setNavPage(false);
    }, [setNavPage]);

    // Email validation function
    const validateEmail = (email) => {
        if (!email || email.trim() === '') {
            return "Email is required"
        }

        // Check length
        if (email.length > 254) {
            return "Email must be no longer than 254 characters"
        }

        // Check for exactly one @ symbol
        const atCount = (email.match(/@/g) || []).length
        if (atCount === 0) {
            return "Email must contain an @ symbol"
        }
        if (atCount > 1) {
            return "Email can only contain one @ symbol"
        }

        // Check email format using regex
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        if (!emailRegex.test(email)) {
            return "Please enter a valid email address"
        }

        return ""
    }

    // Clear errors when email changes
    const handleEmailChange = (e) => {
        setEmail(e.target.value)
        setEmailError("")
    }

    // Clear errors when password changes
    const handlePasswordChange = (e) => {
        setPassword(e.target.value)
        setPasswordError("")
    }

    const handleLogin = useCallback(async (e) => {
        if (e?.preventDefault) {
            e.preventDefault();
        }

        let _email = document.getElementById('emailInput')?.value || email;
        let _password = document.getElementById('passwordInput')?.value || password;

        // Validate email
        const emailValidationError = validateEmail(_email)
        if (emailValidationError) {
            setEmailError(emailValidationError)
            return
        }

        // Validate password
        if (!_password || _password.trim() === '') {
            setPasswordError("Password is required")
            return
        }

        setIsLoading(true);

        function stripHtml(html) {
            const div = document.createElement("div");
            div.innerHTML = html;
            return div.textContent || div.innerText || "";
        }

        try {
            let response;
            let paramJSON = new URLSearchParams(window.location.search);
            let isOAuth = paramJSON.has("redirect_uri") && paramJSON.has("state") && paramJSON.has("client_id");

            if (isOAuth) {
                response = await endpointProvider.post(LINKS.LOGIN.OAUTH_LOGIN, { email: _email, password: _password });
                let redirectUri = decodeURIComponent(`${paramJSON.get("redirect_uri")}?state=${paramJSON.get("state")}&code=${response.code}`);
                window.location.replace(redirectUri);
                return;
            } else {
                const response = await authProvider.login(_email, _password);
                console.log(response);
            }

            setEmailError("");
            setPasswordError("");
            // Navigate to DataVisualize with modal params (works for both desktop and mobile)
            navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?modal=rm&step=SELECT_GATEWAY`);
        } catch (err) {
            let msg;

            if (err?.response?.status >= 500) {
                msg = "Server error (e.g., 503). Please try again later.";
            } else if (typeof err?.response?.data === "string" && err.response.data.includes("<html")) {
                msg = stripHtml(err.response.data);
            } else {
                msg = err?.response?.data?.message ||
                    err?.response?.data ||
                    err?.message ||
                    (typeof err === "string" ? err : null) ||
                    'An error occurred during login';
            }

            setPasswordError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [email, password, navigate, authProvider, endpointProvider, LINKS.LOGIN.OAUTH_LOGIN]);

    if (isLoading) {
        return (
            <LoadingWrapper>
                <LoadingContainer>
                    <MobergLogoSpinner />
                    <LoadingTitle>Loading workspace...</LoadingTitle>
                    <LoadingSubtitle>Please wait while we load your workspace.</LoadingSubtitle>
                </LoadingContainer>
            </LoadingWrapper>
        )
    }

    return (
        <FullScreenWrapper>
            <LoginBox>
                <TopSection style={{ alignItems: "flex-start", paddingTop: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexDirection: "column", alignSelf: "flex-start", width: "100%" }}>
                        <img src={darkLogoImg} alt="Moberg Analytics" style={{ width: '90px', alignSelf: "flex-start" }} />
                        <LoginHeading style={{ fontFamily: "Montserrat", fontWeight: 800, letterSpacing: 1, marginBottom: 0, fontSize: "24px" }}>MOBERG CLINICAL PLATFORM</LoginHeading>
                    </div>
                    <LoginSubheading style={{ fontFamily: MobergFontFamily.REGULAR, letterSpacing: 1, color: "#0097EE", fontSize: "24px", margin: 0, marginBottom: 16, alignSelf: "flex-start" }}>Sign in</LoginSubheading>
                </TopSection>
                <CenterSection>
                    <LoginForm>
                        <LoginInputBox>
                            <LoginLabel htmlFor="emailInput">Email</LoginLabel>
                            <input id="emailInput" type="email" placeholder="Email" value={email || ""} onChange={handleEmailChange} autoComplete="username" />
                            {emailError && <ErrorText><MdError size={16} /> {emailError}</ErrorText>}
                        </LoginInputBox>
                        <LoginInputBox>
                            <LoginLabel htmlFor="passwordInput">Password</LoginLabel>
                            <div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <input id="passwordInput" type={showPassword ? "text" : "password"} placeholder="Password" value={password || ""} onChange={handlePasswordChange} autoComplete="current-password" />
                                <ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
                                </ShowPasswordIcon>
                            </div>
                            {passwordError && <ErrorText><MdError size={16} /> {passwordError}</ErrorText>}
                        </LoginInputBox>
                    </LoginForm>
                </CenterSection>
                <BottomSection>
                    <LoginButton type="button" onClick={handleLogin}>Sign in</LoginButton>
                    <LoginFooter>
                        <div><a href="https://moberganalytics.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: "inherit" }}>MCP v{SOFTWARE_VERSION}</a></div>
                        <div>Copyright © {new Date().getFullYear()} Moberg Analytics, Inc. All rights reserved. <br /> <a href="https://moberganalytics.com/privacy/" target="_blank" rel="noreferrer">Privacy</a></div>
                    </LoginFooter>
                </BottomSection>
            </LoginBox>
        </FullScreenWrapper>
    );
};
// Styled Components
const FullScreenWrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: auto;
`;

const LoginBox = styled.div`
	width: 100%;
	max-width: 450px;
	height: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 40px 32px;
	box-sizing: border-box;
	background: #fff;
	border-radius: 4px;
	box-shadow: 0 2px 16px rgba(0,0,0,0.07);

	@media (max-width: 768px) {
		max-width: 100%;
		padding: 32px 24px;
	}

	@media (max-width: 480px) {
		padding: 24px 20px;
		max-width: 90%;
	}
`;

const TopSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding-top: 0;
	margin-bottom: 16px;
	@media (max-width: 600px) {
		align-items: center;
		margin-bottom: 12px;
	}
`;

const CenterSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 0;
	margin-bottom: 20px;
`;

const BottomSection = styled.div`
	width: 100%;
	padding-bottom: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	@media (max-width: 600px) {
		padding-bottom: 0;
	}
`;

const LoginHeading = styled.h1`
	font-weight: 700;
	margin: 0;
	letter-spacing: 1px;
`;

const LoginSubheading = styled.h2`
	color: #0097EE;
	margin: 0;
	letter-spacing: 1px;
`;

const LoginForm = styled.form`
	width: 100%;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 20px;
	@media (max-width: 600px) {
		width: 100%;
		min-width: 0;
		gap: 18px;
	}
`;

const LoginInputBox = styled.div`
	position: relative;
	margin-bottom: 0;
	input {
		width: 100%;
		padding: 12px 40px 12px 12px;
		border: 1.5px solid #bdbdbd;
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
		outline: none;
		transition: border 0.2s;
		background: #fafbfc;
		&:focus {
			border-color: #0097EE;
		}
		&::-ms-reveal {
			display: none;
		}
	}
`;

const ErrorText = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	color: #d32f2f;
	font-size: 12px;
	font-weight: 500;
	margin-top: 4px;
	margin-left: 2px;
	font-family: inherit;
	svg {
		flex-shrink: 0;
	}
`;

const LoginLabel = styled.label`
	display: block;
	font-size: 16px;
	color: #444;
	font-weight: 500;
	margin-bottom: 6px;
	margin-left: 2px;
	font-family: inherit;
`;

const ShowPasswordIcon = styled.span`
	position: absolute;
	right: 14px;
	cursor: pointer;
	font-size: 1.2rem;
	user-select: none;
	display: flex;
	align-items: center;
	color: #757575;
`;

const LoginButton = styled.button`
	width: 100%;
	padding: 12px 0;
	background: #0097EE;
	color: #fff;
	font-size: 16px;
	font-weight: 600;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	margin-top: 0;
	transition: background 0.2s;
	&:hover:not(:disabled) {
		background: #007fd1;
	}
	&:disabled {
		background: #bdbdbd;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const LoginFooter = styled.div`
	font-size: 12px;
	color: #888;
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 8px;
	div {
		margin: 0;
	}
	a {
		color: #0097EE;
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}
`;

const LoadingWrapper = styled.div`
    background-color: var(--color-surface-primary);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 24px;
`;

const LoadingTitle = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    font-size: 20px;
    color: var(--color-text-primary);
    margin: 0;
    letter-spacing: 0.3px;
`;

const LoadingSubtitle = styled.p`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--text-secondary-color);
    margin: 0;
    line-height: 1.4;
`;

export default RMLogin;
